// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { zodResolver } from "@hookform/resolvers/zod";
import { Dices, Eye, EyeOff, TriangleAlert } from "lucide-react";
import { useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { useUpdateUserMutation, useUserByPublicIdQuery, useUsersByGroupsQuery } from "@/api/hooks/use-users";
import type { components } from "@/api/schema";
import { SaveDiscardPane } from "@/components/shared/save-discard-pane";
import { VirtualMultiSelectPopover } from "@/components/shared/virtual-multi-select-popover";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useAuth } from "@/lib/auth/auth-provider";
import { cn } from "@/lib/utils";

const DIRTY_HIGHLIGHT = "bg-sidebar-accent-hover transition-colors";
const LOCAL_REALM = "local";

// Groups whose members may be assigned as a supervisor (mirrors SUPERVISOR_USER_GROUPS from the
// legacy dashboard). The selector only applies to ops users — i.e. when the edited user belongs to
// a group with an "ops" segment.
const SUPERVISOR_USER_GROUPS = ["ops_vp", "ops_director", "ops_manager"];

function groupsIncludeOps(groups: string[]): boolean {
  return groups.some((name) => name.split("_").includes("ops"));
}

// Password is optional on edit (empty = keep current). When provided for a local-realm user it must
// be at least 8 characters, unless the editor is an admin (who can set any password).
function makeUserEditSchema(isAdmin: boolean, t: (key: string) => string) {
  return z
    .object({
      email: z.email(t("users.form.validation.invalidEmail")),
      fullName: z.string().min(1, t("users.form.validation.required")),
      username: z.string().nullish(),
      realm: z.string().min(1, t("users.form.validation.required")),
      password: z.string().optional(),
      groups: z.array(z.string()),
      flags: z.array(z.string()),
      supervisorUserId: z.string().optional(),
      defaultStore: z.string().optional(),
      availableStores: z.array(z.string()),
    })
    .superRefine((data, ctx) => {
      if (!isAdmin && data.realm === LOCAL_REALM && data.password && data.password.length < 8) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("users.form.validation.passwordMinLength"),
          path: ["password"],
        });
      }
    });
}

type UserEditValues = z.infer<ReturnType<typeof makeUserEditSchema>>;

export interface UserEditTabHandle {
  reset: () => void;
}

interface UserEditTabProps {
  publicId: string;
  onDirtyChange?: (dirty: boolean) => void;
  ref?: React.Ref<UserEditTabHandle>;
}

export function UserEditTab({ publicId, onDirtyChange, ref }: UserEditTabProps) {
  const { t } = useTranslation("back-office");
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.groups.includes("admin") ?? false;
  const { data } = useUserByPublicIdQuery(publicId);
  const { data: context } = useBackofficeContext();
  const updateUser = useUpdateUserMutation(publicId);

  // Active stores (for assigning) merged with the user's own default/available stores so that
  // inactive-but-assigned stores still render their legible name and can be unassigned here.
  const storeOptions = useMemo(() => {
    const byValue = new Map<string, { label: string; value: string }>();
    for (const s of context?.stores ?? []) byValue.set(s.value, { label: s.display, value: s.value });
    for (const s of data?.availableStores ?? []) {
      if (!byValue.has(s.storeName)) byValue.set(s.storeName, { label: s.legibleName, value: s.storeName });
    }
    if (data?.defaultStore && !byValue.has(data.defaultStore.storeName)) {
      byValue.set(data.defaultStore.storeName, {
        label: data.defaultStore.legibleName,
        value: data.defaultStore.storeName,
      });
    }
    return [...byValue.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [context, data]);
  const schema = useMemo(() => makeUserEditSchema(isAdmin, t), [isAdmin, t]);
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<UserEditValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: "",
      fullName: "",
      username: "",
      realm: "",
      password: "",
      groups: [],
      flags: [],
      supervisorUserId: "",
      defaultStore: "",
      availableStores: [],
    },
  });

  const currentRealm = form.watch("realm");
  const isLocalRealm = currentRealm === LOCAL_REALM;
  // Supervisor selector is only relevant for ops users (same rule as the FT FUE flag).
  const selectedGroups = form.watch("groups");
  const showSupervisor = useMemo(() => groupsIncludeOps(selectedGroups), [selectedGroups]);
  const { data: supervisorCandidates } = useUsersByGroupsQuery(SUPERVISOR_USER_GROUPS, showSupervisor);
  // Candidate options — exclude the user being edited (nobody supervises themselves).
  const supervisorOptions = useMemo(
    () =>
      (supervisorCandidates ?? [])
        .filter((candidate) => candidate.userId !== data?.user.userId)
        .map((candidate) => ({ label: candidate.fullName || candidate.email, value: String(candidate.userId) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [supervisorCandidates, data?.user.userId],
  );
  // Warn when moving a local-realm user to an SSO realm (their password will be wiped on save)
  const showRealmChangeWarning = data?.user.realm === LOCAL_REALM && !!currentRealm && currentRealm !== LOCAL_REALM;

  useEffect(
    function resetFormWhenUserDataLoads() {
      if (data) {
        form.reset({
          email: data.user.email,
          fullName: data.user.fullName ?? "",
          username: data.user.userName ?? "",
          realm: data.user.realm,
          password: "",
          groups: data.groups,
          flags: data.flags,
          supervisorUserId: data.supervisor?.userId != null ? String(data.supervisor.userId) : "",
          defaultStore: data.defaultStore?.storeName ?? "",
          availableStores: data.availableStores.map((s) => s.storeName),
        });
      }
    },
    [data, form],
  );

  async function onSubmit(values: UserEditValues) {
    try {
      await updateUser.mutateAsync({
        email: values.email,
        userName: values.username || undefined,
        fullName: values.fullName,
        realm: values.realm,
        password: values.password || undefined,
        groups: values.groups,
        flags: values.flags as components["schemas"]["UserFlagEnum"][],
        // Only reconcile the supervisor for ops users; omit otherwise so it's left unchanged.
        // Empty selection clears it (null); a chosen value sets it.
        ...(showSupervisor
          ? { supervisorUserId: values.supervisorUserId ? Number(values.supervisorUserId) : null }
          : {}),
        defaultStoreId: values.defaultStore || undefined,
        availableStoreIds: values.availableStores,
      });
      toast.success(t("users.notifications.updated", { name: values.fullName || values.email }));
      // Reset to the form's own current values so dirty state clears on 200 (independent of refetch)
      form.reset(form.getValues());
    } catch {
      // Error toast handled by API middleware
    }
  }

  const { isDirty, dirtyFields } = form.formState;

  useImperativeHandle(ref, () => ({ reset: () => form.reset() }));

  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  useEffect(
    function notifyParentOfDirtyState() {
      onDirtyChangeRef.current?.(isDirty);
    },
    [isDirty],
  );

  return (
    <div className="max-w-2xl space-y-6 p-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {/* Email — only editable by admins */}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("users.form.email")}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    {...field}
                    disabled={!isAdmin}
                    autoComplete="off"
                    data-1p-ignore
                    data-lpignore="true"
                    className={cn(dirtyFields.email && DIRTY_HIGHLIGHT)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="fullName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("users.form.fullName")}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    autoComplete="off"
                    data-1p-ignore
                    data-lpignore="true"
                    className={cn(dirtyFields.fullName && DIRTY_HIGHLIGHT)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("users.form.username")}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    autoComplete="off"
                    data-1p-ignore
                    data-lpignore="true"
                    className={cn(dirtyFields.username && DIRTY_HIGHLIGHT)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="realm"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("users.form.realm")}</FormLabel>
                <FormControl>
                  <VirtualSelectPopover
                    value={field.value}
                    onChange={field.onChange}
                    options={(context?.userRealms ?? []).map((r) => ({ label: r, value: r }))}
                    placeholder={t("users.form.selectRealm")}
                    searchPlaceholder={t("users.form.searchRealms")}
                    emptyText={t("users.form.noRealms")}
                    className={cn(dirtyFields.realm && DIRTY_HIGHLIGHT)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {showRealmChangeWarning && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
              <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{t("users.form.realmChangeWarning")}</span>
            </div>
          )}

          {isLocalRealm && (
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel>{t("users.form.password")}</FormLabel>
                    {isAdmin && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-xs leading-none text-muted-foreground cursor-help underline decoration-dotted underline-offset-2">
                            {t("users.form.validationDisabled")}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top">{t("users.form.validationDisabledHint")}</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        data-1p-ignore
                        data-lpignore="true"
                        placeholder={t("users.form.passwordEditPlaceholder")}
                        className={cn("pr-16", dirtyFields.password && DIRTY_HIGHLIGHT)}
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-8 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground"
                        onClick={() => {
                          const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*";
                          const array = new Uint32Array(16);
                          crypto.getRandomValues(array);
                          const password = Array.from(array, (v) => chars[v % chars.length]).join("");
                          field.onChange(password);
                          setShowPassword(true);
                        }}
                        tabIndex={-1}
                      >
                        <Dices className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowPassword((prev) => !prev)}
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <Separator />

          <FormField
            control={form.control}
            name="groups"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("users.form.groups")}</FormLabel>
                <FormControl>
                  <VirtualMultiSelectPopover
                    values={field.value}
                    onChange={field.onChange}
                    options={(context?.userGroups ?? []).map((g) => ({ label: g, value: g }))}
                    placeholder={t("users.form.selectGroups")}
                    searchPlaceholder={t("users.form.searchGroups")}
                    emptyText={t("users.form.noGroups")}
                    className={cn(dirtyFields.groups && DIRTY_HIGHLIGHT)}
                    dirty={!!dirtyFields.groups}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {showSupervisor && (
            <FormField
              control={form.control}
              name="supervisorUserId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("users.form.supervisor")}</FormLabel>
                  <FormControl>
                    <VirtualSelectPopover
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      options={supervisorOptions}
                      placeholder={t("users.form.selectSupervisor")}
                      searchPlaceholder={t("users.form.searchSupervisor")}
                      emptyText={t("users.form.noSupervisor")}
                      clearable
                      className={cn(dirtyFields.supervisorUserId && DIRTY_HIGHLIGHT)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <Separator />

          <FormField
            control={form.control}
            name="defaultStore"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("users.form.defaultStore")}</FormLabel>
                <FormControl>
                  <VirtualSelectPopover
                    value={field.value ?? ""}
                    onChange={(v) => {
                      const previousDefault = field.value;
                      field.onChange(v);
                      // Drop the previous default from available, and keep the new default in it
                      let available = form.getValues("availableStores");
                      if (previousDefault) available = available.filter((s) => s !== previousDefault);
                      if (v && !available.includes(v)) available = [...available, v];
                      form.setValue("availableStores", available, { shouldDirty: true });
                    }}
                    options={storeOptions}
                    placeholder={t("users.form.selectStore")}
                    searchPlaceholder={t("users.form.searchStores")}
                    emptyText={t("users.form.noStores")}
                    className={cn(dirtyFields.defaultStore && DIRTY_HIGHLIGHT)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="availableStores"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("users.form.availableStores")}</FormLabel>
                <FormControl>
                  <VirtualMultiSelectPopover
                    values={field.value}
                    onChange={(list) => {
                      // The default store must always remain in the available stores
                      const defaultStore = form.getValues("defaultStore");
                      field.onChange(defaultStore && !list.includes(defaultStore) ? [defaultStore, ...list] : list);
                    }}
                    options={storeOptions}
                    placeholder={t("users.form.selectAvailableStores")}
                    searchPlaceholder={t("users.form.searchStores")}
                    emptyText={t("users.form.noStores")}
                    className={cn(dirtyFields.availableStores && DIRTY_HIGHLIGHT)}
                    dirty={!!dirtyFields.availableStores}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Separator />

          <FormField
            control={form.control}
            name="flags"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("users.form.flags")}</FormLabel>
                <FormControl>
                  <VirtualMultiSelectPopover
                    values={field.value}
                    onChange={field.onChange}
                    options={(context?.userFlags ?? []).map((f) => ({ label: f, value: f }))}
                    placeholder={t("users.form.selectFlags")}
                    searchPlaceholder={t("users.form.searchFlags")}
                    emptyText={t("users.form.noFlags")}
                    className={cn(dirtyFields.flags && DIRTY_HIGHLIGHT)}
                    dirty={!!dirtyFields.flags}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {isDirty && <SaveDiscardPane onDiscard={() => form.reset()} saving={updateUser.isPending} />}
        </form>
      </Form>
    </div>
  );
}
